import React, { useState } from "react";
import { Button, Divider, Drawer, Radio, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { UserProfile } from "../../Entities/UserProfile";
import ResumeOtherInfo from "./ResumeOtherInfo";
import ResumeProject from "./ResumeProject";
import ResumeSkills from "./ResumeSkills";
import ResumeRawFilePreview from "./ResumeRawFilePreview";
import ResumeBasicDetails from "./ResumeBasicDetails";
import ResumeSummary from "./ResumeSummary";
import ResumeExperience from "./ResumeExperience";
import ResumeEducation from "./ResumeEducation";
import "./index.scss";

interface CandidateResumePreviewProps {
  onClose: () => void;
  isOpen: boolean;
  userProfile: UserProfile;
}

const CandidateResumePreview: React.FC<CandidateResumePreviewProps> = ({
  isOpen,
  onClose,
  userProfile,
}) => {
  const [view, setView] = useState<"extracted" | "raw">("extracted");

  if (!userProfile) {
    return null;
  }

  const {
    basicDetails,
    socialLinks,
    summary,
    experience,
    education,
    skills,
    projects,
    otherDetails,
    certifications,
    languages,
    achievements,
  } = userProfile;

  const isOtherDetailsAvailable = otherDetails && otherDetails.trim() !== "";
  const isCertificationsAvailable = certifications && certifications.length > 0;
  const isAchievementsAvailable = achievements && achievements.length > 0;
  const isLanguagesAvailable = languages && languages.length > 0;
  const isShowOtherDetails =
    isOtherDetailsAvailable ||
    isCertificationsAvailable ||
    isAchievementsAvailable ||
    isLanguagesAvailable;

  return (
    <Drawer
      placement="right"
      width={"50%"}
      open={isOpen}
      onClose={onClose}
      destroyOnHidden
      title={null}
      closeIcon={false}
    >
      <div className="resume-preview-wrapper">
        <div className="resume-preview-header">
          <Typography.Title level={4} style={{ margin: 0 }}>
            Resume Preview
          </Typography.Title>
          <div className="resume-preview-header-actions">
            <Radio.Group
              value={view}
              optionType="button"
              buttonStyle="solid"
              onChange={(e) => setView(e.target.value)}
              options={[
                { value: "extracted", label: "Extracted" },
                { value: "raw", label: "Raw" },
              ]}
            />
            <Button
              icon={<CloseOutlined />}
              type="text"
              danger
              onClick={onClose}
            />
          </div>
        </div>
        <Divider style={{ margin: 0 }} />
        {view === "extracted" ? (
          <div className="resume-preview-content">
            {basicDetails && (
              <ResumeBasicDetails
                basicDetails={basicDetails}
                socials={socialLinks}
              />
            )}
            {summary && summary.trim() !== "" && (
              <ResumeSummary summary={summary} />
            )}
            {experience && experience.length > 0 && (
              <ResumeExperience experience={experience || []} />
            )}
            {education && education.length > 0 && (
              <ResumeEducation educations={education || []} />
            )}
            {skills && skills.length > 0 && <ResumeSkills skills={skills} />}
            {projects && projects.length > 0 && (
              <ResumeProject projects={projects} />
            )}
            {isShowOtherDetails && (
              <ResumeOtherInfo
                otherDetails={otherDetails}
                languages={languages}
                achievements={achievements}
                certifications={certifications}
              />
            )}
          </div>
        ) : (
          <div className="resume-preview-content resume-preview-content--raw">
            <ResumeRawFilePreview userProfile={userProfile} />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default CandidateResumePreview;
